// src/components/CourseManagerDialog.jsx
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Chip,
  Divider,
  Grid,
  TextField,
  IconButton,
} from "@mui/material";
import { Add, Close, UploadFile } from "@mui/icons-material";
import { useEffect, useState, useMemo } from "react";
import { validatePdfFormFields } from "../../../helpers/ExportPDF";

export default function CourseFileManagerDialog({
  open,
  onClose,
  onSave,
  selectedCourses = [],
  availableCourses = [],
  onUpdateConstancia,
  onUploadFile,
  onRemoveCourse,
  onAddCourse,
}) {
  const [localAvailableCourses, setLocalAvailableCourses] = useState([]);
  const [errors, setErrors] = useState({});

  // 🔄 sincroniza los cursos disponibles cuando cambian en el padre
  useEffect(() => {
    if (open && availableCourses.length > 0) {
      setLocalAvailableCourses(availableCourses);
    }
  }, [open, availableCourses]);

  const categories = useMemo(
    () => [...new Set(localAvailableCourses.map((item) => item.category))].sort(),
    [localAvailableCourses]
  );

  // 📄 Validación de archivo PDF
  const handleFileChange = async (cursoCode, file) => {
    if (file && file.type !== "application/pdf") {
      alert("Solo se permiten archivos PDF.");
      return;
    }
    const requiredFields = ["nombre", "identificacion", "dia", "mes", "ano"];
    const result = await validatePdfFormFields(file, requiredFields);

    if (!result.valid) {
      alert(result.message);
      return
    } else {
      console.log("✅ PDF válido y completo");
    }

    onUploadFile(cursoCode, file);
  };

  // 🧩 Validar todos los campos
  useEffect(() => {
    const newErrors = {};

    selectedCourses.forEach((curso) => {
      const code = curso.code;
      const constancia = curso.constancia?.trim() || "";
      const file = curso.file;

      // validar nombre de constancia
      if (!constancia) {
        newErrors[code] = "El nombre de constancia es obligatorio.";
      } else if (!/^[A-Za-z0-9_]+$/.test(constancia)) {
        newErrors[code] = "Solo letras, números o guion bajo (_).";
      } else if (constancia.length > 10) {
        newErrors[code] = "Máximo 10 caracteres.";
      }

      // validar archivo PDF
      if (!file) {
        newErrors[code + "_file"] = "Debe subir un archivo PDF.";
      }
    });

    setErrors(newErrors);
  }, [selectedCourses]);

  // 🚫 Bloquear botón Guardar si hay errores o faltan datos
  const hasErrors = Object.keys(errors).length > 0;

  const handleSave = () => {
    if (hasErrors) {
      alert("Corrige los errores antes de guardar.");
      return;
    }
    onSave();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontSize: "1rem" }}>
        Agregar constancias
      </DialogTitle>

      <DialogContent>
        {/* Cursos Seleccionados */}
        <Typography
          variant="body2"
          fontWeight="bold"
          sx={{ mb: 1.5, fontSize: "0.85rem" }}
        >
          Cursos Seleccionados:
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            mb: 2,
            minHeight: 50,
            maxHeight: 250,
            overflowY: "auto",
            p: 1.5,
            border: "1px solid #E5E7EB",
            borderRadius: 1.5,
            backgroundColor: "#F9FAFB",
            "&::-webkit-scrollbar": { width: "4px" },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#D1D5DB",
              borderRadius: "2px",
              "&:hover": { backgroundColor: "#9CA3AF" },
            },
          }}
        >
          {selectedCourses.length === 0 ? (
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ alignSelf: "center", fontSize: "0.75rem" }}
            >
              No hay cursos seleccionados
            </Typography>
          ) : (
            selectedCourses.map((curso) => (
              <Box
                key={curso.code}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  p: 1,
                  backgroundColor: "white",
                  borderRadius: 1.5,
                  border: "1px solid #E5E7EB",
                }}
              >
                {/* Encabezado con nombre del curso */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Chip
                    label={`${curso.code} - ${curso.name}`}
                    size="small"
                    sx={{
                      backgroundColor: "#10B981",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "0.7rem",
                    }}
                  />
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => onRemoveCourse(curso.code)}
                    sx={{
                      p: 0.5,
                      "&:hover": { backgroundColor: "rgba(244, 67, 54, 0.1)" },
                    }}
                  >
                    <Close sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>

                {/* Campos: Nombre constancia + PDF opcional */}
                <Grid container spacing={1.5}>
                  {/* Nombre constancia */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Nombre de constancia"
                      value={curso.constancia || ""}
                      onChange={(e) =>
                        onUpdateConstancia(curso.code, e.target.value)
                      }
                      placeholder="Ej: constancia1"
                      inputProps={{
                        maxLength: 10,
                        pattern: "^[A-Za-z0-9_]+$",
                      }}
                      error={Boolean(errors[curso.code])}
                      helperText={
                        errors[curso.code] ||
                        "Máx. 10 caracteres alfanuméricos o '_'"
                      }
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "#F9FAFB",
                          fontSize: "0.8rem",
                        },
                      }}
                    />
                  </Grid>

                  {/* Subir PDF */}
                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Button
                        component="label"
                        variant="outlined"
                        size="small"
                        startIcon={<UploadFile sx={{ fontSize: 16 }} />}
                        sx={{
                          textTransform: "none",
                          borderColor: "#D1D5DB",
                          fontSize: "0.75rem",
                          "&:hover": { borderColor: "#9CA3AF" },
                        }}
                      >
                        Subir PDF
                        <input
                          hidden
                          type="file"
                          accept=".pdf"
                          onChange={(e) =>
                            handleFileChange(curso.code, e.target.files[0])
                          }
                        />
                      </Button>

                      {curso.file ? (
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "0.7rem",
                            color: "#374151",
                            maxWidth: 150,
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                          }}
                          title={curso.file.name}
                        >
                          {curso.file.name}
                        </Typography>
                      ) : (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ fontSize: "0.7rem" }}
                        >
                          {errors[curso.code + "_file"]
                            ? "Archivo PDF requerido"
                            : "(opcional)"}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            ))
          )}
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Cursos Disponibles */}
        <Typography
          variant="body2"
          fontWeight="bold"
          sx={{ mb: 1.5, fontSize: "0.85rem" }}
        >
          Cursos Disponibles:
        </Typography>

        {/* Organizado por categorías */}
        <Box sx={{ mb: 1.5 }}>
          {categories.map((category, index) => (
            <Box key={category + index}>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{
                  mb: 0.8,
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  display: "block",
                }}
              >
                {category}
              </Typography>

              <Grid container spacing={0.8} sx={{ mb: 1.5 }}>
                {localAvailableCourses
                  .filter((curso) => curso.category === category)
                  .map((curso) => (
                    <Grid item xs={12} sm={6} md={4} key={curso.code}>
                      <Chip
                        label={`${curso.code} - ${curso.name}`}
                        onClick={() => onAddCourse(curso)}
                        icon={<Add sx={{ fontSize: 12 }} />}
                        size="small"
                        sx={{
                          backgroundColor: "#F3F4F6",
                          "&:hover": { backgroundColor: "#E5E7EB" },
                          cursor: "pointer",
                          width: "100%",
                          justifyContent: "flex-start",
                          "& .MuiChip-label": {
                            fontSize: "0.7rem",
                          },
                        }}
                      />
                    </Grid>
                  ))}
              </Grid>
            </Box>
          ))}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} size="small">
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          size="small"
          sx={{
            backgroundColor: hasErrors ? "#9CA3AF" : "#10B981",
            pointerEvents: hasErrors ? "none" : "auto",
          }}
        >
          Guardar Cambios
        </Button>
      </DialogActions>
    </Dialog>
  );
}
